import {
  useClient,
  useCreateSalesShare,
  useGenerateSalesDocument,
  useResolveCompliance,
  useSalesComplianceProfile,
  useSalesLot,
  useSalesLotAction,
  useSalesShareAction,
} from '@/api/hooks';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/lib/api';
import type { SalesShare } from '@/types/api';
import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

export function SalesLotDetailPage() {
  const { lotId } = useParams();
  const id = Number(lotId);
  const { data: client, isLoading: clientLoading } = useClient();
  const { data: lot, isLoading } = useSalesLot(id);
  const lotAction = useSalesLotAction();
  const createShare = useCreateSalesShare(id);
  const shareAction = useSalesShareAction(id);
  const generateDoc = useGenerateSalesDocument(id);
  const { data: compliance } = useSalesComplianceProfile();
  const resolveCompliance = useResolveCompliance();

  const [shareForm, setShareForm] = useState({
    fraction: 'quarter',
    quantity: '1',
    cut_type: '',
    unit_label: '',
    price: '',
    weight_kg: '',
    notes: '',
  });
  const [buyerModal, setBuyerModal] = useState<SalesShare | null>(null);
  const [buyerForm, setBuyerForm] = useState({ name: '', phone: '', email: '' });
  const [cutShare, setCutShare] = useState<SalesShare | null>(null);
  const [cutForm, setCutForm] = useState({
    steaks: true,
    ground_beef: false,
    ribs: false,
    roast: false,
    chops: false,
    stew_cuts: false,
    thickness_inches: '1',
    packaging_notes: '',
  });
  const [docHtml, setDocHtml] = useState<string | null>(null);
  const [answers, setAnswers] = useState<string[]>([]);
  const [step, setStep] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    share: SalesShare;
    action: 'mark-delivered' | 'unmark-delivered';
  } | null>(null);

  async function runShareAction(
    shareId: number,
    action: 'mark-signal' | 'mark-balance' | 'mark-delivered' | 'unmark-delivered',
  ) {
    setActionError(null);
    try {
      await shareAction.mutateAsync({ shareId, action });
      setConfirmAction(null);
    } catch (err) {
      setActionError(getErrorMessage(err, 'Não foi possível atualizar a cota.'));
    }
  }

  const confirmCopy =
    confirmAction?.action === 'mark-delivered'
      ? {
          title: 'Confirmar entrega',
          body: `Marcar a cota ${confirmAction.share.fraction_display} de ${confirmAction.share.buyer?.name || 'comprador'} como entregue?`,
          confirmLabel: 'Sim, entregar',
        }
      : confirmAction?.action === 'unmark-delivered'
        ? {
            title: 'Desfazer entrega',
            body: `Remover a marcação de entregue da cota ${confirmAction.share.fraction_display}? O status volta para pago/reservado.`,
            confirmLabel: 'Sim, desfazer',
          }
        : null;

  const profile = compliance?.profile;
  const questions = profile?.questions ?? [];
  const outcome = resolveCompliance.data?.outcome;

  const shares = lot?.shares ?? [];

  const progressLabel = useMemo(() => {
    if (!lot) return '';
    return `${lot.shares_sold}/${lot.shares_count} cotas · ${lot.progress_percent}%`;
  }, [lot]);

  if (!clientLoading && !client?.modules?.animal_sales) {
    return <Navigate to="/" replace />;
  }

  if (isLoading || !lot) {
    return <p className="text-sm text-manejai-bark/60">Carregando lote…</p>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={lot.title}
        subtitle={progressLabel}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/vendas">
              <Button variant="ghost" type="button">
                Voltar
              </Button>
            </Link>
            {lot.status === 'draft' && (
              <Button
                type="button"
                loading={lotAction.isPending}
                onClick={() => lotAction.mutate({ id: lot.id, action: 'publish' })}
              >
                Abrir para vendas
              </Button>
            )}
            {['open', 'full'].includes(lot.status) && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => lotAction.mutate({ id: lot.id, action: 'slaughter' })}
              >
                Marcar abatido
              </Button>
            )}
            {['full', 'slaughtered'].includes(lot.status) && (
              <Button
                type="button"
                onClick={() => lotAction.mutate({ id: lot.id, action: 'deliver' })}
              >
                Finalizar entrega
              </Button>
            )}
          </div>
        }
      />

      <div className="rounded-2xl border border-manejai-sand bg-white p-4 text-sm">
        <div className="flex flex-wrap gap-4">
          <div>
            <span className="text-manejai-bark/50">Tipo</span>
            <div className="font-medium">{lot.sale_type_label}</div>
          </div>
          <div>
            <span className="text-manejai-bark/50">Preço</span>
            <div className="font-medium">{lot.pricing_unit_label}</div>
          </div>
          <div>
            <span className="text-manejai-bark/50">Origem</span>
            <div className="font-medium">
              {lot.source_lot
                ? `Lote ${lot.source_lot.name} (${lot.source_lot.animals_count} animais)`
                : lot.animal?.name || (lot.animal_id ? `#${lot.animal_id}` : '—')}
            </div>
          </div>
          <div>
            <span className="text-manejai-bark/50">Abate</span>
            <div className="font-medium">{lot.slaughter_date || '—'}</div>
          </div>
          <div>
            <span className="text-manejai-bark/50">Retirada</span>
            <div className="font-medium">{lot.pickup_location || '—'}</div>
          </div>
          {lot.establishment_number && (
            <div>
              <span className="text-manejai-bark/50">Estabelecimento</span>
              <div className="font-medium">{lot.establishment_number}</div>
            </div>
          )}
          {lot.public_url && (
            <div>
              <span className="text-manejai-bark/50">Página pública</span>
              <div>
                <a className="text-manejai-forest underline" href={lot.public_url} target="_blank" rel="noreferrer">
                  {lot.public_slug}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-manejai-forest">
          {lot.sale_type === 'live_animal'
            ? 'Animais / unidades'
            : lot.sale_type === 'retail_cuts'
              ? 'Cortes / pacotes'
              : 'Cotas'}
        </h2>
        {actionError && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {actionError}
          </p>
        )}
        <form
          className="grid gap-2 rounded-2xl border border-dashed border-manejai-sand p-3 md:grid-cols-5"
          onSubmit={async (e) => {
            e.preventDefault();
            setActionError(null);
            try {
              const body: Record<string, unknown> = {
                price: Number(shareForm.price),
                weight_kg: shareForm.weight_kg ? Number(shareForm.weight_kg) : null,
                notes: shareForm.notes || null,
              };
              if (lot.sale_type === 'carcass_share') {
                body.fraction = shareForm.fraction;
              } else if (lot.sale_type === 'live_animal') {
                body.quantity = Number(shareForm.quantity) || 1;
              } else {
                body.cut_type = shareForm.cut_type || null;
                body.unit_label = shareForm.unit_label || shareForm.cut_type || null;
              }
              await createShare.mutateAsync(body);
              setShareForm({
                fraction: 'quarter',
                quantity: '1',
                cut_type: '',
                unit_label: '',
                price: '',
                weight_kg: '',
                notes: '',
              });
            } catch (err) {
              setActionError(getErrorMessage(err, 'Não foi possível adicionar o item.'));
            }
          }}
        >
          {lot.sale_type === 'carcass_share' && (
            <Select
              label="Fração"
              value={shareForm.fraction}
              options={[
                { value: 'whole', label: 'Inteiro' },
                { value: 'half', label: '1/2' },
                { value: 'quarter', label: '1/4' },
                { value: 'eighth', label: '1/8' },
                { value: 'custom', label: 'Custom' },
              ]}
              onChange={(e) => setShareForm((f) => ({ ...f, fraction: e.target.value }))}
            />
          )}
          {lot.sale_type === 'live_animal' && (
            <Input
              label="Cabeças"
              type="number"
              min="1"
              step="1"
              value={shareForm.quantity}
              onChange={(e) => setShareForm((f) => ({ ...f, quantity: e.target.value }))}
            />
          )}
          {lot.sale_type === 'retail_cuts' && (
            <>
              <Select
                label="Tipo de corte"
                value={shareForm.cut_type}
                options={[
                  { value: 'ribeye', label: 'Ribeye' },
                  { value: 'ground_beef', label: 'Moído' },
                  { value: 'ribs', label: 'Costela' },
                  { value: 'loin', label: 'Lombo' },
                  { value: 'other', label: 'Outro' },
                ]}
                onChange={(e) => setShareForm((f) => ({ ...f, cut_type: e.target.value }))}
              />
              <Input
                label="Rótulo do pacote"
                value={shareForm.unit_label}
                onChange={(e) => setShareForm((f) => ({ ...f, unit_label: e.target.value }))}
              />
            </>
          )}
          <Input
            label="Preço"
            type="number"
            step="0.01"
            value={shareForm.price}
            onChange={(e) => setShareForm((f) => ({ ...f, price: e.target.value }))}
            required
          />
          <Input
            label={lot.sale_type === 'carcass_share' ? 'Peso (custom)' : 'Peso (kg)'}
            type="number"
            step="0.01"
            value={shareForm.weight_kg}
            onChange={(e) => setShareForm((f) => ({ ...f, weight_kg: e.target.value }))}
          />
          <Input
            label="Notas"
            value={shareForm.notes}
            onChange={(e) => setShareForm((f) => ({ ...f, notes: e.target.value }))}
          />
          <div className="flex items-end">
            <Button type="submit" loading={createShare.isPending} className="w-full">
              Adicionar
            </Button>
          </div>
        </form>

        <div className="grid gap-3">
          {shares.map((share) => {
            const hasBuyer = Boolean(share.buyer_id || share.buyer);
            return (
            <div key={share.id} className="rounded-2xl border border-manejai-sand bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium">
                    {share.display_label || share.fraction_display} · R${' '}
                    {Number(share.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                  <div className="text-sm text-manejai-bark/70">
                    {share.buyer?.name || 'Sem comprador'}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-manejai-bark/60">
                    {share.signal_paid_at && <span>Sinal ✓</span>}
                    {share.balance_paid_at && <span>Saldo ✓</span>}
                    {share.delivered_at && <span>Entregue ✓</span>}
                    {!hasBuyer && (
                      <span className="text-amber-700">Vincule um comprador para marcar pagamentos</span>
                    )}
                  </div>
                </div>
                <Badge
                  tone={
                    share.status === 'available'
                      ? 'default'
                      : share.status === 'reserved'
                        ? 'warning'
                        : 'success'
                  }
                >
                  {share.status}
                </Badge>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setBuyerModal(share)}>
                  Vincular comprador
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!hasBuyer || Boolean(share.signal_paid_at)}
                  loading={shareAction.isPending}
                  title={!hasBuyer ? 'Vincule um comprador primeiro' : undefined}
                  onClick={() => runShareAction(share.id, 'mark-signal')}
                >
                  Sinal pago
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!hasBuyer || Boolean(share.balance_paid_at)}
                  loading={shareAction.isPending}
                  title={!hasBuyer ? 'Vincule um comprador primeiro' : undefined}
                  onClick={() => runShareAction(share.id, 'mark-balance')}
                >
                  Saldo pago
                </Button>
                {lot.requires_cut_sheet && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setCutShare(share);
                      if (share.cut_sheet) {
                        setCutForm({
                          steaks: share.cut_sheet.steaks,
                          ground_beef: share.cut_sheet.ground_beef,
                          ribs: share.cut_sheet.ribs,
                          roast: share.cut_sheet.roast,
                          chops: share.cut_sheet.chops,
                          stew_cuts: share.cut_sheet.stew_cuts,
                          thickness_inches: String(share.cut_sheet.thickness_inches ?? ''),
                          packaging_notes: share.cut_sheet.packaging_notes ?? '',
                        });
                      }
                    }}
                  >
                    Ficha de corte
                  </Button>
                )}
                {share.delivered_at ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={shareAction.isPending}
                    onClick={() => setConfirmAction({ share, action: 'unmark-delivered' })}
                  >
                    Desfazer entrega
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!hasBuyer}
                    loading={shareAction.isPending}
                    title={!hasBuyer ? 'Vincule um comprador primeiro' : undefined}
                    onClick={() => setConfirmAction({ share, action: 'mark-delivered' })}
                  >
                    Entregue
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="secondary"
                  loading={generateDoc.isPending}
                  onClick={async () => {
                    const res = await generateDoc.mutateAsync({
                      type: 'bill_of_sale',
                      shareId: share.id,
                    });
                    setDocHtml(res.document.content);
                  }}
                >
                  Bill of Sale
                </Button>
                {lot.sale_type !== 'live_animal' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      try {
                        const res = await generateDoc.mutateAsync({
                          type: 'label_text',
                          shareId: share.id,
                        });
                        setDocHtml(res.document.content);
                      } catch (err) {
                        setActionError(getErrorMessage(err, 'Não foi possível gerar o rótulo.'));
                      }
                    }}
                  >
                    Rótulo
                  </Button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {profile && (
        <section className="rounded-2xl border border-manejai-sand bg-white p-4 space-y-3">
          <h2 className="text-lg font-semibold text-manejai-forest">
            Assistente de compliance ({profile.country_code})
          </h2>

          {lot.sale_type === 'live_animal' && (
            <div className="space-y-2">
              <Badge tone="info">Animal vivo</Badge>
              <p className="text-sm text-manejai-bark">
                Venda de animal vivo tem compliance mínimo. Gere um bill of sale simples de
                transferência de propriedade. Não há restrição de abatedouro, pois a
                responsabilidade do abate é do comprador.
              </p>
              <p className="text-xs text-manejai-bark/60">{profile.disclaimer}</p>
            </div>
          )}

          {lot.sale_type === 'retail_cuts' && (
            <div className="space-y-2">
              <Badge tone={lot.slaughterhouse_type === 'custom_exempt' ? 'danger' : 'warning'}>
                Cortes finais
              </Badge>
              <p className="text-sm text-manejai-bark">
                Venda de cortes finais exige abatedouro com Grant of Inspection (USDA ou DSHS
                Texas) e rotulagem completa do produto. Abatedouro customizado (custom exempt)
                NÃO pode ser usado para este tipo de venda.
              </p>
              {lot.slaughterhouse_type === 'custom_exempt' && (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  Atenção: custom exempt normalmente não cobre venda de cortes finais. Preencha o
                  nº do estabelecimento se for inspecionado (opcional, não bloqueia publicação).
                </p>
              )}
              {!lot.establishment_number && (
                <p className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-600">
                  Nº do estabelecimento ainda não preenchido (opcional).
                </p>
              )}
              <p className="text-xs text-manejai-bark/60">{profile.disclaimer}</p>
            </div>
          )}

          {lot.sale_type === 'carcass_share' &&
            (step < questions.length ? (
              <div className="space-y-3">
                <p className="font-medium">{questions[step].prompt}</p>
                <div className="grid gap-2">
                  {questions[step].options.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      className="rounded-xl border border-manejai-sand px-3 py-2 text-left text-sm hover:border-manejai-sage"
                      onClick={async () => {
                        const next = [...answers.slice(0, step), opt.value];
                        setAnswers(next);
                        if (step + 1 >= questions.length) {
                          await resolveCompliance.mutateAsync(next);
                          setStep(step + 1);
                        } else {
                          setStep(step + 1);
                        }
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {outcome && (
                  <>
                    <Badge
                      tone={
                        outcome.level === 'ok'
                          ? 'success'
                          : outcome.level === 'warning'
                            ? 'warning'
                            : outcome.level === 'blocked'
                              ? 'danger'
                              : 'info'
                      }
                    >
                      {outcome.title}
                    </Badge>
                    <p className="text-sm text-manejai-bark">{outcome.body}</p>
                  </>
                )}
                <p className="text-xs text-manejai-bark/60">{profile.disclaimer}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setAnswers([]);
                    setStep(0);
                    resolveCompliance.reset();
                  }}
                >
                  Refazer
                </Button>
              </div>
            ))}
        </section>
      )}

      <Modal
        open={!!confirmAction && !!confirmCopy}
        onClose={() => setConfirmAction(null)}
        title={confirmCopy?.title ?? ''}
        size="sm"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setConfirmAction(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant={confirmAction?.action === 'unmark-delivered' ? 'danger' : 'primary'}
              loading={shareAction.isPending}
              onClick={() => {
                if (!confirmAction) return;
                void runShareAction(confirmAction.share.id, confirmAction.action);
              }}
            >
              {confirmCopy?.confirmLabel}
            </Button>
          </>
        }
      >
        <p className="text-sm text-manejai-bark">{confirmCopy?.body}</p>
      </Modal>

      <Modal
        open={!!buyerModal}
        onClose={() => setBuyerModal(null)}
        title="Vincular comprador"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setBuyerModal(null)}>
              Cancelar
            </Button>
            <Button type="submit" form="assign-buyer-form-modal" loading={shareAction.isPending}>
              Salvar
            </Button>
          </>
        }
      >
        <form
          id="assign-buyer-form-modal"
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!buyerModal) return;
            await shareAction.mutateAsync({
              shareId: buyerModal.id,
              action: 'assign-buyer',
              body: buyerForm,
            });
            setBuyerModal(null);
            setBuyerForm({ name: '', phone: '', email: '' });
          }}
        >
          <Input
            label="Nome"
            value={buyerForm.name}
            onChange={(e) => setBuyerForm((f) => ({ ...f, name: e.target.value }))}
            required
          />
          <Input
            label="Telefone"
            value={buyerForm.phone}
            onChange={(e) => setBuyerForm((f) => ({ ...f, phone: e.target.value }))}
          />
          <Input
            label="E-mail"
            type="email"
            value={buyerForm.email}
            onChange={(e) => setBuyerForm((f) => ({ ...f, email: e.target.value }))}
          />
        </form>
      </Modal>

      <Modal
        open={!!cutShare}
        onClose={() => setCutShare(null)}
        title="Ficha de corte"
        footer={
          <Button type="submit" form="cut-sheet-form-modal" loading={shareAction.isPending}>
            Salvar ficha
          </Button>
        }
      >
        <form
          id="cut-sheet-form-modal"
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!cutShare) return;
            await shareAction.mutateAsync({
              shareId: cutShare.id,
              action: 'cut-sheet',
              body: {
                ...cutForm,
                thickness_inches: cutForm.thickness_inches
                  ? Number(cutForm.thickness_inches)
                  : null,
              },
            });
            setCutShare(null);
          }}
        >
          {(
            [
              ['steaks', 'Bifes'],
              ['ground_beef', 'Moída'],
              ['ribs', 'Costela'],
              ['roast', 'Assado'],
              ['chops', 'Costeletas'],
              ['stew_cuts', 'Ensopado'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={cutForm[key]}
                onChange={(e) => setCutForm((f) => ({ ...f, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
          <Input
            label="Espessura (pol)"
            value={cutForm.thickness_inches}
            onChange={(e) => setCutForm((f) => ({ ...f, thickness_inches: e.target.value }))}
          />
          <Input
            label="Embalagem / notas"
            value={cutForm.packaging_notes}
            onChange={(e) => setCutForm((f) => ({ ...f, packaging_notes: e.target.value }))}
          />
        </form>
      </Modal>

      <Modal
        open={!!docHtml}
        onClose={() => setDocHtml(null)}
        title="Documento"
        size="lg"
        footer={
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                const w = window.open('', '_blank');
                if (w && docHtml) {
                  w.document.write(docHtml);
                  w.document.close();
                  w.print();
                }
              }}
            >
              Imprimir
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                if (docHtml) {
                  const text = docHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                  await navigator.clipboard.writeText(text);
                }
              }}
            >
              Copiar texto
            </Button>
          </>
        }
      >
        <div
          className="max-h-[60vh] overflow-auto rounded-xl border border-manejai-sand bg-white p-4"
          dangerouslySetInnerHTML={{ __html: docHtml || '' }}
        />
      </Modal>
    </div>
  );
}
